import React, { useState, useRef, useMemo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline'
import {
  RelationshipType,
  LineageType,
  type FamilyMember,
  type FamilyTreeOwner,
  DIRECT_RELATIONSHIPS,
  IN_LAW_RELATIONSHIPS,
  impliedGender,
  sortedSpouses,
  spouseOrdinalMap as buildOrdinalMap,
  filledInLawSlots,
  familyTreeService,
} from '../../services/family-tree.service'
import { ordinalSuffix } from './MemberCard'
import { ADDRESS_STATE_NAMES, getCitiesForStateName } from '../../constants/malaysian-states'

interface AddMemberModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
  owner: FamilyTreeOwner | null
  members: FamilyMember[]
}

interface FormValues {
  relationship: RelationshipType | ''
  targetSpouseId: string
  firstName: string
  lastName: string
  gender: 'MALE' | 'FEMALE'
  dateOfBirth: string
  isDeceased: string
  dateOfDeath: string
  lineageType: LineageType
  passportNumber: string
  email: string
  address: string
  city: string
  state: string
  postalCode: string
}

const LINEAGE_RELS = new Set<RelationshipType>([
  RelationshipType.FATHER,
  RelationshipType.MOTHER,
  RelationshipType.CHILD,
  RelationshipType.SIBLING,
  RelationshipType.FATHER_IN_LAW,
  RelationshipType.MOTHER_IN_LAW,
])

const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf'
const MAX_BYTES = 10 * 1024 * 1024

export default function AddMemberModal({
  isOpen,
  onClose,
  onCreated,
  owner,
  members,
}: AddMemberModalProps) {
  const { t } = useTranslation()
  const [submitting, setSubmitting] = useState(false)
  const [relQuery, setRelQuery] = useState('')
  const [relOpen, setRelOpen] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(0)
  const [wifePickStep, setWifePickStep] = useState(false)
  const relBoxRef = useRef<HTMLDivElement>(null)
  const birthFileRef = useRef<HTMLInputElement>(null)
  const icFileRef = useRef<HTMLInputElement>(null)
  const [birthFile, setBirthFile] = useState<File | null>(null)
  const [icFile, setIcFile] = useState<File | null>(null)

  const { register, handleSubmit, watch, setValue, reset, formState } = useForm<FormValues>({
    defaultValues: {
      relationship: '',
      targetSpouseId: '',
      firstName: '',
      lastName: '',
      gender: 'MALE',
      dateOfBirth: '',
      isDeceased: 'false',
      dateOfDeath: '',
      lineageType: LineageType.BIOLOGICAL,
      passportNumber: '',
      email: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
    },
  })

  const rel = watch('relationship') as RelationshipType | ''
  const watchedTargetSpouseId = watch('targetSpouseId')
  const watchedLineageType = watch('lineageType') as LineageType
  const watchedIsDeceased = watch('isDeceased')
  const watchState = watch('state')
  const ownerGender = owner?.gender ?? null
  const spouses = useMemo(
    () => members.filter((m) => m.relationship === RelationshipType.SPOUSE),
    [members],
  )
  const spousesSorted = useMemo(() => sortedSpouses(members), [members])
  const ordinalMap = useMemo(() => buildOrdinalMap(members), [members])
  const selectedSpouseName = useMemo(() => {
    if (!watchedTargetSpouseId) return ''
    const sp = spouses.find((s) => s.id === watchedTargetSpouseId)
    if (!sp) return ''
    const ord = ordinalMap.get(sp.id) ?? 0
    return `${sp.firstName}${sp.lastName ? ' ' + sp.lastName : ''}${ordinalSuffix(ord)}`
  }, [watchedTargetSpouseId, spouses, ordinalMap])

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      reset()
      setRelQuery('')
      setRelOpen(false)
      setWifePickStep(false)
      setBirthFile(null)
      setIcFile(null)
      if (icFileRef.current) icFileRef.current.value = ''
      if (birthFileRef.current) birthFileRef.current.value = ''
    }
  }, [isOpen, reset])

  // Close combobox on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (relBoxRef.current && !relBoxRef.current.contains(e.target as Node)) {
        setRelOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  // ---- Availability / capacity filter for relationship picker ----
  const availability = useMemo(() => {
    const map = new Map<RelationshipType, { ok: boolean; reason?: string }>()
    const bioFather = members.find(
      (m) =>
        m.relationship === RelationshipType.FATHER && m.lineageType === LineageType.BIOLOGICAL,
    )
    const bioMother = members.find(
      (m) =>
        m.relationship === RelationshipType.MOTHER && m.lineageType === LineageType.BIOLOGICAL,
    )
    for (const r of DIRECT_RELATIONSHIPS) map.set(r, { ok: true })
    for (const r of IN_LAW_RELATIONSHIPS) map.set(r, { ok: true })

    if (bioFather) map.set(RelationshipType.FATHER, { ok: false, reason: t('familyTree.err.bioFatherExists') })
    if (bioMother) map.set(RelationshipType.MOTHER, { ok: false, reason: t('familyTree.err.bioMotherExists') })

    // Spouse cardinality: female owner capped at 1
    if (ownerGender === 'FEMALE' && spouses.length >= 1) {
      map.set(RelationshipType.SPOUSE, { ok: false, reason: t('familyTree.err.spouseExists') })
    }
    // In-laws need a spouse
    if (spouses.length === 0) {
      for (const r of IN_LAW_RELATIONSHIPS) {
        map.set(r, { ok: false, reason: t('familyTree.err.needsSpouseFirst') })
      }
    }
    // Per-spouse in-law parent slots (when a wife is already selected)
    if (watchedTargetSpouseId) {
      const filled = filledInLawSlots(watchedTargetSpouseId, members)
      if (filled.has(RelationshipType.FATHER_IN_LAW)) {
        map.set(RelationshipType.FATHER_IN_LAW, { ok: false, reason: t('familyTree.err.bioFatherExists') })
      }
      if (filled.has(RelationshipType.MOTHER_IN_LAW)) {
        map.set(RelationshipType.MOTHER_IN_LAW, { ok: false, reason: t('familyTree.err.bioMotherExists') })
      }
    }
    return map
  }, [members, ownerGender, spouses.length, t, watchedTargetSpouseId])

  // Combobox options (filtered by query string against the i18n label).
  const filteredOptions = useMemo(() => {
    const labelFor = (r: RelationshipType) => {
      const implied = impliedGender(r)
      const key = implied
        ? r
        : r === RelationshipType.SPOUSE
          ? ownerGender === 'MALE'
            ? 'wife'
            : ownerGender === 'FEMALE'
              ? 'husband'
              : 'SPOUSE'
          : r
      return t(`familyTree.rels.${key}`)
    }
    const q = relQuery.trim().toLowerCase()
    const build = (rels: RelationshipType[]) =>
      rels
        .filter((r) => availability.get(r)?.ok !== false)
        .filter((r) => (q ? labelFor(r).toLowerCase().includes(q) : true))
        .map((r) => ({ rel: r, label: labelFor(r) }))

    return {
      direct: build(DIRECT_RELATIONSHIPS),
      inLaws: build(IN_LAW_RELATIONSHIPS),
    }
  }, [relQuery, availability, ownerGender, t])

  const flatOptions = useMemo(
    () => [...filteredOptions.direct, ...filteredOptions.inLaws],
    [filteredOptions],
  )

  useEffect(() => {
    setHighlightIdx(0)
  }, [relQuery, relOpen])

  // ---- Gender logic ----
  const genderLocked = useMemo<null | 'MALE' | 'FEMALE'>(() => {
    if (!rel) return null
    if (rel === RelationshipType.SPOUSE) {
      // opposite of owner; if owner gender missing, caller blocks submit
      return ownerGender === 'MALE' ? 'FEMALE' : ownerGender === 'FEMALE' ? 'MALE' : null
    }
    return impliedGender(rel)
  }, [rel, ownerGender])

  useEffect(() => {
    if (genderLocked) setValue('gender', genderLocked)
  }, [genderLocked, setValue])

  // ---- Dynamic context area: show wife-pick step for in-laws with >1 spouse ----
  const isInLawRel = !!rel && IN_LAW_RELATIONSHIPS.includes(rel as RelationshipType)

  // ---- Conflict pre-check: target spouse already has a bio father/mother in-law of this slot ----
  const conflictReason = useMemo<string | null>(() => {
    if (!rel) return null
    const targetId = watchedTargetSpouseId || (spouses.length === 1 ? spouses[0].id : '')
    if (
      rel === RelationshipType.FATHER_IN_LAW &&
      watchedLineageType === LineageType.BIOLOGICAL
    ) {
      const exists = members.some(
        (m) =>
          m.relationship === RelationshipType.FATHER_IN_LAW &&
          m.lineageType === LineageType.BIOLOGICAL &&
          m.targetSpouseId === targetId,
      )
      if (exists) return t('familyTree.err.bioFatherExists')
    }
    if (
      rel === RelationshipType.MOTHER_IN_LAW &&
      watchedLineageType === LineageType.BIOLOGICAL
    ) {
      const exists = members.some(
        (m) =>
          m.relationship === RelationshipType.MOTHER_IN_LAW &&
          m.lineageType === LineageType.BIOLOGICAL &&
          m.targetSpouseId === targetId,
      )
      if (exists) return t('familyTree.err.bioMotherExists')
    }
    return null
  }, [rel, members, spouses, t, watchedTargetSpouseId, watchedLineageType])

  const spouseMissingGender =
    rel === RelationshipType.SPOUSE && !ownerGender ? t('familyTree.err.needsGenderFirst') : null
  const submitBlocked = !!conflictReason || !!spouseMissingGender

  if (!isOpen) return null

  const onPickRel = (r: RelationshipType) => {
    setValue('relationship', r)
    setRelQuery('')
    setRelOpen(false)
    // Enter wife-pick step if in-law selected and multiple spouses exist
    if (IN_LAW_RELATIONSHIPS.includes(r) && spouses.length > 1) {
      setWifePickStep(true)
      setValue('targetSpouseId', '')
    } else {
      setWifePickStep(false)
    }
  }

  const onRelKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setRelOpen(true)
      setHighlightIdx((i) => Math.min(i + 1, Math.max(flatOptions.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setRelOpen(true)
      setHighlightIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      if (relOpen && flatOptions[highlightIdx]) {
        e.preventDefault()
        onPickRel(flatOptions[highlightIdx].rel)
      }
    } else if (e.key === 'Escape') {
      setRelOpen(false)
    }
  }

  const validateFile = (f: File): string | null => {
    if (f.size > MAX_BYTES) return t('familyTree.err.fileTooLarge')
    const ok = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(f.type)
    return ok ? null : t('familyTree.err.unsupportedType')
  }

  const onPickBirth = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    const err = validateFile(f)
    if (err) {
      toast.error(err)
      if (birthFileRef.current) birthFileRef.current.value = ''
      return
    }
    setBirthFile(f)
  }
  const onPickIc = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    const err = validateFile(f)
    if (err) {
      toast.error(err)
      if (icFileRef.current) icFileRef.current.value = ''
      return
    }
    setIcFile(f)
  }

  const onSubmit = async (data: FormValues) => {
    if (!data.relationship) {
      toast.error(t('familyTree.selectRelationship'))
      return
    }
    const targetSpouseId =
      data.targetSpouseId || (spouses.length === 1 ? spouses[0].id : undefined)
    const isDeceasedBool = String(data.isDeceased) === 'true'
    if (
      data.relationship &&
      IN_LAW_RELATIONSHIPS.includes(data.relationship as RelationshipType) &&
      !targetSpouseId
    ) {
      toast.error(t('familyTree.err.selectSpouse'))
      return
    }
    setSubmitting(true)
    try {
      const created = await familyTreeService.addMember({
        relationship: data.relationship as RelationshipType,
        lineageType: data.lineageType,
        targetSpouseId: targetSpouseId || undefined,
        firstName: data.firstName.trim(),
        lastName: data.lastName?.trim() || undefined,
        gender: data.gender,
        dateOfBirth: data.dateOfBirth || undefined,
        isDeceased: isDeceasedBool,
        dateOfDeath: isDeceasedBool ? data.dateOfDeath || undefined : undefined,
        passportNumber: data.passportNumber?.trim() || undefined,
        email: data.email?.trim() || undefined,
        address: data.address?.trim() || undefined,
        city: data.city?.trim() || undefined,
        state: data.state?.trim() || undefined,
        postalCode: data.postalCode?.trim() || undefined,
      })

      // Create-then-Upload: upload any selected files against the new member id
      if (birthFile) {
        try {
          await familyTreeService.uploadDocument(created.id, birthFile, 'BIRTH_CERTIFICATE')
        } catch (e: any) {
          toast.error(e?.response?.data?.message || t('familyTree.err.unsupportedType'))
        }
      }
      if (icFile) {
        try {
          await familyTreeService.uploadDocument(created.id, icFile, 'IC_PASSPORT')
        } catch (e: any) {
          toast.error(e?.response?.data?.message || t('familyTree.err.unsupportedType'))
        }
      }

      toast.success(t('familyTree.addMember'))
      onCreated()
      onClose()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  const showLineage = !!rel && LINEAGE_RELS.has(rel as RelationshipType)

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-neutral-200 p-5">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">{t('familyTree.addMember')}</h2>
            <p className="text-xs text-neutral-500">{t('familyTree.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
            disabled={submitting}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 p-5">
          {/* Relationship combobox */}
          <div>
            <label className="block text-sm font-medium text-neutral-700">
              {t('familyTree.relationship')} <span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1" ref={relBoxRef}>
              <input
                type="text"
                className="input w-full"
                placeholder={t('familyTree.selectRelationship')}
                value={relOpen ? relQuery : rel ? t(`familyTree.rels.${rel === RelationshipType.SPOUSE ? (ownerGender === 'MALE' ? 'wife' : ownerGender === 'FEMALE' ? 'husband' : 'SPOUSE') : rel}`) : ''}
                onChange={(e) => {
                  setRelQuery(e.target.value)
                  setRelOpen(true)
                }}
                onFocus={() => setRelOpen(true)}
                onKeyDown={onRelKey}
              />
              {relOpen && (
                <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-neutral-200 bg-white shadow-lg">
                  {filteredOptions.direct.length > 0 && (
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                      {t('familyTree.directFamily')}
                    </div>
                  )}
                  {filteredOptions.direct.map((o) => (
                    <button
                      type="button"
                      key={o.rel}
                      onClick={() => onPickRel(o.rel)}
                      onMouseEnter={() =>
                        setHighlightIdx(flatOptions.findIndex((x) => x.rel === o.rel))
                      }
                      className={`block w-full px-3 py-2 text-left text-sm hover:bg-primary-50 ${
                        flatOptions[highlightIdx]?.rel === o.rel ? 'bg-primary-50' : ''
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                  {filteredOptions.inLaws.length > 0 && (
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                      {t('familyTree.inLaws')}
                    </div>
                  )}
                  {filteredOptions.inLaws.map((o) => (
                    <button
                      type="button"
                      key={o.rel}
                      onClick={() => onPickRel(o.rel)}
                      onMouseEnter={() =>
                        setHighlightIdx(flatOptions.findIndex((x) => x.rel === o.rel))
                      }
                      className={`block w-full px-3 py-2 text-left text-sm hover:bg-primary-50 ${
                        flatOptions[highlightIdx]?.rel === o.rel ? 'bg-primary-50' : ''
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                  {flatOptions.length === 0 && (
                    <div className="px-3 py-2 text-sm text-neutral-400">—</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Wife-pick step — replaces form body for in-laws with multiple spouses */}
          {wifePickStep && isInLawRel && (
            <div className="rounded-md border border-primary-300 bg-primary-50 p-4">
              <h3 className="text-sm font-semibold text-primary-900">
                {t('familyTree.pickWifeFirst')}
              </h3>
              <p className="mt-1 text-xs text-primary-700">
                {t('familyTree.pickWifeSubtitle')}
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {spousesSorted.map((sp) => {
                  const ord = ordinalMap.get(sp.id) ?? 0
                  const filled = filledInLawSlots(sp.id, members)
                  return (
                    <button
                      type="button"
                      key={sp.id}
                      onClick={() => {
                        setValue('targetSpouseId', sp.id)
                        setWifePickStep(false)
                      }}
                      className="rounded-lg border border-neutral-200 bg-white p-3 text-left hover:border-primary-400 hover:shadow-sm transition"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${sp.gender === 'MALE' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                          {sp.gender === 'MALE' ? '♂' : '♀'}
                        </span>
                        <span className="text-sm font-medium text-neutral-900">
                          {sp.firstName}{sp.lastName ? ' ' + sp.lastName : ''}
                        </span>
                        <span className="text-xs text-neutral-500">
                          {t(`familyTree.rels.${sp.gender === 'MALE' ? 'husband' : 'wife'}`)}{ordinalSuffix(ord)}
                        </span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        <span className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${filled.has(RelationshipType.FATHER_IN_LAW) ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-400'}`}>
                          {t('familyTree.rels.FATHER_IN_LAW')} {filled.has(RelationshipType.FATHER_IN_LAW) ? '✓' : '—'}
                        </span>
                        <span className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${filled.has(RelationshipType.MOTHER_IN_LAW) ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-400'}`}>
                          {t('familyTree.rels.MOTHER_IN_LAW')} {filled.has(RelationshipType.MOTHER_IN_LAW) ? '✓' : '—'}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
              <button
                type="button"
                onClick={() => {
                  setValue('relationship', '')
                  setWifePickStep(false)
                }}
                className="mt-3 text-xs text-neutral-500 hover:underline"
              >
                {t('common.back') || 'Back'}
              </button>
            </div>
          )}

          {/* Confirmation bar — shows which wife was selected, with change button */}
          {!wifePickStep && isInLawRel && watchedTargetSpouseId && spouses.length > 1 && (
            <div className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
              <span className="text-xs text-primary-800">
                {t('familyTree.addingFor')}: <strong>{selectedSpouseName}</strong>
              </span>
              <button
                type="button"
                onClick={() => setWifePickStep(true)}
                className="text-xs text-primary-600 hover:underline"
              >
                {t('familyTree.change')}
              </button>
            </div>
          )}

          {spouseMissingGender && (
            <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              {spouseMissingGender}
            </p>
          )}

          {/* Form body — hidden during wife-pick step */}
          {!wifePickStep && (
          <>
          {/* Personal details */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.firstName')} <span className="text-red-500">*</span>
              </label>
              <input
                className={`input mt-1 w-full ${formState.errors.firstName ? 'border-red-300' : ''}`}
                {...register('firstName', { required: true, minLength: 1 })}
              />
              {formState.errors.firstName && (
                <p className="mt-1 text-xs text-red-600">{t('familyTree.firstName')}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.lastName')}
              </label>
              <input className="input mt-1 w-full" {...register('lastName')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.gender')}
              </label>
              <select
                className="input mt-1 w-full disabled:bg-neutral-100"
                disabled={!!genderLocked}
                {...register('gender')}
              >
                <option value="MALE">{t('familyTree.male')}</option>
                <option value="FEMALE">{t('familyTree.female')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.dateOfBirth')}
              </label>
              <input type="date" className="input mt-1 w-full" {...register('dateOfBirth')} />
            </div>
          </div>

          {/* Lineage */}
          {showLineage && (
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.lineage')}
              </label>
              <div className="mt-1 flex gap-3">
                {[LineageType.BIOLOGICAL, LineageType.ADOPTIVE].map((lt) => (
                  <label
                    key={lt}
                    className="flex flex-1 cursor-pointer items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm"
                  >
                    <input
                      type="radio"
                      value={lt}
                      {...register('lineageType')}
                      className="text-primary-600"
                    />
                    {lt === LineageType.BIOLOGICAL
                      ? t('familyTree.biological')
                      : t('familyTree.adoptive')}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Life status */}
          <div>
            <label className="block text-sm font-medium text-neutral-700">
              {t('familyTree.lifeStatus')}
            </label>
            <div className="mt-1 flex gap-3">
              <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm">
                <input
                  type="radio"
                  value="false"
                  {...register('isDeceased')}
                  className="text-primary-600"
                />
                {t('familyTree.living')}
              </label>
              <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-md border border-neutral-200 px-3 py-2 text-sm">
                <input
                  type="radio"
                  value="true"
                  {...register('isDeceased')}
                  className="text-primary-600"
                />
                {t('familyTree.deceased')}
              </label>
            </div>
            {String(watchedIsDeceased) === 'true' && (
              <div className="mt-2">
                <label className="block text-sm font-medium text-neutral-700">
                  {t('familyTree.dateOfDeath')}
                </label>
                <input type="date" className="input mt-1 w-full sm:max-w-xs" {...register('dateOfDeath')} />
              </div>
            )}
          </div>

          {/* Identity & address */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.passportNumber')}
              </label>
              <input className="input mt-1 w-full" {...register('passportNumber')} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.email')}
              </label>
              <input className="input mt-1 w-full" type="email" {...register('email')} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.address')}
              </label>
              <input className="input mt-1 w-full" {...register('address')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">{t('familyTree.city')}</label>
              <input
                className="input mt-1 w-full"
                list="add-member-city-options"
                {...register('city')}
              />
              <datalist id="add-member-city-options">
                {getCitiesForStateName(watchState || '').map((city) => (
                  <option key={city} value={city} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">{t('familyTree.state')}</label>
              <select className="input mt-1 w-full" {...register('state')}>
                <option value="">Select State</option>
                {ADDRESS_STATE_NAMES.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                {t('familyTree.postalCode')}
              </label>
              <input className="input mt-1 w-full" {...register('postalCode')} />
            </div>
          </div>

          {/* Optional documents */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DocSlot
              label={t('familyTree.birthCertificate')}
              onPick={onPickBirth}
              inputRef={birthFileRef}
              fileName={birthFile?.name}
              onClear={() => {
                setBirthFile(null)
                if (birthFileRef.current) birthFileRef.current.value = ''
              }}
              t={t}
            />
            <DocSlot
              label={t('familyTree.nricPassport')}
              onPick={onPickIc}
              inputRef={icFileRef}
              fileName={icFile?.name}
              onClear={() => {
                setIcFile(null)
                if (icFileRef.current) icFileRef.current.value = ''
              }}
              t={t}
            />
          </div>
          <p className="text-xs text-neutral-400">{t('familyTree.maxSize')}</p>

          {conflictReason && (
            <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{conflictReason}</p>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-neutral-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              {t('familyTree.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting || submitBlocked}
              className="rounded-md bg-primary-600 px-5 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t('familyTree.saving') : t('familyTree.save')}
            </button>
          </div>
          </>
          )}
        </form>
      </div>
    </div>
  )
}

function DocSlot({
  label,
  onPick,
  inputRef,
  fileName,
  onClear,
  t,
}: {
  label: string
  onPick: (e: React.ChangeEvent<HTMLInputElement>) => void
  inputRef: React.RefObject<HTMLInputElement | null>
  fileName?: string
  onClear: () => void
  t: (k: string) => string
}) {
  return (
    <div className="rounded-md border border-neutral-200 p-3">
      <p className="text-sm font-medium text-neutral-700">{label}</p>
      {!fileName ? (
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
          >
            <CloudArrowUpIcon className="h-4 w-4" />
            {t('familyTree.upload')}
          </button>
          <input ref={inputRef} type="file" accept={ACCEPT} onChange={onPick} className="hidden" />
        </div>
      ) : (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="truncate text-xs text-neutral-600">{fileName}</span>
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-red-600 hover:underline"
          >
            {t('familyTree.removeDocument')}
          </button>
        </div>
      )}
    </div>
  )
}
